/**
 * German items (D-118), as on the answer sheet of the Goethe-Institut: Richtig / Falsch, Ja / Nein,
 * stimmt / stimmt nicht / nicht im Text, a / b / c (/ d), the choice between two ads (a or b) and
 * the matching with a shared reserve (a–j, « 0 » for none). The texts, examples (with their
 * solution) and reserves of a part. The test content stays in German (`lang="de"`).
 */

import { academyAttemptsAnswerCreate } from "@agencyos/api-client";
import { type GoetheExample, type GoethePassage, type GoetheQuestion, hasPictures, optionLabel, optionPictureLetter, textsOf } from "@agencyos/core";
import { Check } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ExamPictureView, PictureOptions, PicturePool, poolHasPictures } from "../ExamPicture";

export const LETTERS = "abcdefghij";

const TWO_STATE = new Set(["rf", "ja_nein", "three_way"]);

export function letterOf(index: number): string {
  return LETTERS[index] ?? String(index + 1);
}

/** One item: its number, its statement (or the author for B1 Lesen part 4) and its choices. */
export function GoetheItem({
  question,
  passage,
  value,
  onChange,
  disabled = false,
  answer,
  showTexts = false,
}: {
  question: GoetheQuestion;
  passage: GoethePassage | null;
  value: number | null;
  onChange?: (choice: number) => void;
  disabled?: boolean;
  /** In a review: the right answer, shown with the learner's choice. */
  answer?: number | null;
  showTexts?: boolean;
}) {
  const { t } = useTranslation();
  const name = `goethe-q-${question.id}`;
  const linked = showTexts ? textsOf(question, passage) : [];
  const match = question.format === "match";
  const mark = (index: number) => (answer === undefined || answer === null ? "" : index === answer ? " ac-goethe-choice--right" : index === value ? " ac-goethe-choice--wrong" : "");
  return (
    <li className="ac-goethe-item" id={`goethe-item-${question.number}`}>
      <p className="ac-goethe-item__stem" lang="de">
        <span className="ac-goethe-item__n ao-num" aria-hidden="true">
          {question.number}
        </span>
        <span id={`${name}-label`}>{question.stem}</span>
      </p>
      <ExamPictureView picture={question.image} size="medium" />
      {linked.length > 0 && (
        <div className="ac-goethe-item__texts">
          {linked.map((text, index) => (
            <p key={text.id} className="ac-goethe-ad" lang="de">
              {question.textIds.length > 1 && <strong>{letterOf(index)}</strong>} {text.text}
            </p>
          ))}
        </div>
      )}
      {!match && hasPictures(question.optionImages) ? (
        // A2 Hören Teil 3: the pictures a, b, c (the text of an option only as its alt).
        <PictureOptions
          name={name}
          options={question.options}
          images={question.optionImages}
          labels={question.options.map(optionPictureLetter)}
          value={value}
          onChange={answer === undefined ? onChange : undefined}
          disabled={disabled}
          answer={answer ?? null}
          given={answer === undefined ? null : value}
          showText={answer !== undefined}
          legend={question.stem}
        />
      ) : match ? (
        <div className={`ac-goethe-match${answer !== undefined ? " ac-goethe-match--review" : ""}`} role="radiogroup" aria-labelledby={`${name}-label`}>
          {question.options.map((option, index) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={value === index}
              aria-label={optionLabel(option, passage?.pool ?? [])}
              title={optionLabel(option, passage?.pool ?? [])}
              disabled={disabled}
              className={`ac-goethe-key${value === index ? " ac-goethe-key--on" : ""}${mark(index)}`}
              onClick={() => onChange?.(index)}
            >
              {option}
            </button>
          ))}
        </div>
      ) : (
        <div className={TWO_STATE.has(question.format) ? "ac-goethe-binary" : "ac-goethe-choices"} role="radiogroup" aria-labelledby={`${name}-label`} lang="de">
          {question.options.map((option, index) => (
            <button
              key={`${index}-${option}`}
              type="button"
              role="radio"
              aria-checked={value === index}
              disabled={disabled}
              className={`ac-goethe-choice${value === index ? " ac-goethe-choice--on" : ""}${mark(index)}`}
              onClick={() => onChange?.(index)}
            >
              {!TWO_STATE.has(question.format) && <span className="ac-goethe-choice__letter">{letterOf(index)}</span>}
              <span>{option}</span>
              {value === index && <Check aria-hidden size={14} className="ac-goethe-choice__tick" />}
            </button>
          ))}
        </div>
      )}
      {answer !== undefined && value === null && <p className="ac-footnote">{t("goethe.review.noAnswer")}</p>}
    </li>
  );
}

/** The example of a part, with its solution (as on the official paper). */
export function GoetheExampleCard({ example, passage }: { example: GoetheExample; passage: GoethePassage | null }) {
  const { t, i18n } = useTranslation();
  const solution =
    example.answerKey ? optionLabel(example.answerKey, passage?.pool ?? []) : example.answerText ? example.answerText : example.answer !== null ? `${letterOf(example.answer)}) ${example.options[example.answer] ?? ""}` : "";
  const help = i18n.language.startsWith("en") ? example.explanationEn || example.explanationFr : example.explanationFr || example.explanationEn;
  return (
    <aside className="ac-goethe-example" aria-label={t("goethe.player.example")}>
      <p className="ac-goethe-example__title">
        <span className="ac-goethe-item__n ao-num">0</span> {t("goethe.player.example")}
      </p>
      {example.prompt && (
        <p lang="de" className="ac-goethe-example__prompt">
          {example.prompt}
        </p>
      )}
      {passage && hasPictures(passage.exampleOptionImages) && (
        <PictureOptions
          name={`goethe-example-${passage.id}`}
          options={example.options}
          images={passage.exampleOptionImages}
          labels={example.options.map(optionPictureLetter)}
          answer={example.answer}
          legend={t("academy.picture.example")}
        />
      )}
      {solution && (
        <p lang="de">
          <strong>{t("goethe.player.solution")}</strong> {solution}
        </p>
      )}
      {help && <p className="ac-footnote">{help}</p>}
    </aside>
  );
}

/** The texts of a Lesen part (an e-mail, a blog, signs, comments, an article with gaps). */
export function GoetheTexts({ passage }: { passage: GoethePassage }) {
  if (!passage.texts.length) return null;
  return (
    <div className="ac-goethe-texts" lang="de">
      {passage.texts.map((text) => (
        <article key={text.id} className="ac-goethe-text">
          {(text.title || text.author) && <h3 className="ac-goethe-text__title">{text.title || text.author}</h3>}
          <p>{text.text}</p>
        </article>
      ))}
    </div>
  );
}

/** The shared reserve of a matching part (ads, sentences, opinions, headings, speakers). */
export function GoethePool({ passage }: { passage: GoethePassage }) {
  const { t } = useTranslation();
  if (!passage.pool.length) return null;
  // A2 Hören Teil 2: the pictures a–i to match (3 columns), the text only for screen readers.
  if (poolHasPictures(passage.pool)) return <PicturePool items={passage.pool} label={t("goethe.player.pool")} />;
  return (
    <dl className="ac-goethe-pool" aria-label={t("goethe.player.pool")} lang="de">
      {passage.pool.map((item) => (
        <div key={item.key} className="ac-goethe-pool__row">
          <dt className="ao-num">{item.key}</dt>
          <dd>{item.text}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Saves the answers of a sitting (one request per change, the last one wins). */
export function useGoetheAnswers(attemptId: string, initial: Record<string, number | null>) {
  const [values, setValues] = useState<Record<string, number | null>>(initial);
  const [error, setError] = useState(false);
  const pending = useRef(new Map<string, Promise<unknown>>());
  const set = useCallback(
    (question: GoetheQuestion, choice: number) => {
      setValues((v) => ({ ...v, [question.id]: choice }));
      const request = academyAttemptsAnswerCreate(attemptId, { question: question.id, response: choice })
        .then(() => setError(false))
        .catch(() => setError(true));
      pending.current.set(question.id, request);
    },
    [attemptId],
  );
  const flush = useCallback(async () => {
    await Promise.allSettled([...pending.current.values()]);
  }, []);
  const valueOf = (question: GoetheQuestion) => (question.id in values ? (values[question.id] ?? null) : question.response);
  return { set, flush, valueOf, error };
}
